import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ChevronDown, ChevronUp, ClipboardList, MapPin, UserPlus } from '@digital-twins/design-system/icons'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { Heading } from '@/components/ui/Heading'
import { ChatScrollContainer } from '@/components/chat/ChatScrollContainer'
import { ChatComposer } from '@/components/chat/ChatComposer'
import { ConversationEmptyState } from '@/components/chat/ConversationEmptyState/ConversationEmptyState'
import { ConversationStage } from '@/components/chat/ConversationStage/ConversationStage'
import { ChatBubble, getBubblePosition } from '@/components/chat/ChatBubble'
import { ChatTypingDots } from '@/components/chat/ChatTypingDots'
import { CollapsibleText, SidebarDivider } from '@/components/chat/SidebarPrimitives'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { getUserInitials } from '@/lib/utils/avatar'
import { buildLocationLine } from '@/lib/utils/twin-blurb'
import { cn } from '@/lib/utils'
import { blockedFeatureMessage } from '@/lib/blocked-feature-error'
import { publicApi } from '@/lib/api'
import { useRealtimeRepair } from '@/lib/realtime/useRealtimeRepair'
import { incrementPollWonRepair } from '@/lib/realtime/pollWonMetrics'
import { useGuestChatRoomSocket } from '@/lib/realtime/useGuestChatRoomSocket'
import { extractCleanSurveyAnswers } from '@/lib/utils/clean-survey-answers'
import { resolveSurveyAnswerPairs, SurveyAnswerPair, SurveyChoiceLabelMap } from '@/lib/utils/survey-answer-display'
import {
  AnswerGroundingMeter,
  readAnswerGrounding,
} from '@/pages/ChatPage/ChatThread/AnswerGroundingMeter/AnswerGroundingMeter'

import { ChatRoomSenderType } from '@digital-twins/shared-types'
import { endsWaitingForReply } from '@/features/studies/sharing/page/components/endsWaitingForReply'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChatMessage {
  sequenceIndex: number
  senderType: ChatRoomSenderType
  senderParticipantId: string | null
  content: string
  mode: 'chat' | 'sample' | null
  metadataType: string | null
  metadata: unknown
  createdAt: string
}

interface RespondentProfile {
  respondent_id: string
  name: string
  image_url: string
  summary: string
  tagline?: string
  respondent_attributes: Record<string, any>
  survey_data: Record<string, any>
}

export interface GuestTwinChatProps {
  shareToken: string
  participantId: string
  profile: RespondentProfile
  surveyQuestionsMap: Record<string, string>
  surveyChoiceLabels?: SurveyChoiceLabelMap
  onBack: () => void
}

const MAX_WAIT_MS = 90_000
const FALLBACK_IMAGE = '/images/twin-fallback.png'

// ─── Survey Answers Sub-components ────────────────────────────────────────────

function GuestSurveyAnswersPanel({
  pairs,
  onClose,
  className,
}: {
  pairs: Array<SurveyAnswerPair>
  onClose: () => void
  className?: string
}) {
  const { t } = useTranslation(['app', 'common'])

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl bg-twin-chat-surface',
        className,
      )}
      data-testid="guest-survey-answers-panel"
    >
      <div className="flex shrink-0 items-center justify-between border-b border-twin-chat-divider/20 px-5 py-4">
        <Text size="sm" className="font-bold text-twin-chat-fg">
          {t('studyReport.surveyAnswers')}
        </Text>
        <button
          type="button"
          onClick={onClose}
          data-testid="guest-survey-answers-close"
          className="flex h-7 w-7 items-center justify-center rounded-md text-twin-chat-muted transition-colors hover:text-twin-chat-fg hover:bg-twin-chat-bubble/20"
        >
          <span className="sr-only">{t('close', { ns: 'common' })}</span>
          ×
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-overlay p-5">
        {pairs.length === 0 ? (
          <Text size="sm" className="text-twin-chat-muted italic">
            {t('studyReport.noSurveyAnswers')}
          </Text>
        ) : (
          <div className="flex flex-col gap-4">
            {pairs.map((pair) => (
              <div key={pair.question} className="flex flex-col gap-0.5">
                <Text size="sm" className="font-medium text-twin-chat-muted">
                  {pair.question}
                </Text>
                {pair.groups ? (
                  <div className="flex flex-col gap-1">
                    {pair.groups.map((group, index) => (
                      <Text key={index} size="sm" className="text-twin-chat-fg">
                        {group}
                      </Text>
                    ))}
                  </div>
                ) : (
                  <Text size="sm" className="text-twin-chat-fg">
                    {pair.answer}
                  </Text>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function GuestTwinChat({
  shareToken,
  participantId,
  profile,
  surveyQuestionsMap,
  surveyChoiceLabels,
  onBack,
}: GuestTwinChatProps) {
  const { t } = useTranslation(['app', 'common'])
  const [messages, setMessages] = useState<Array<ChatMessage>>([])
  const [waitingForReply, setWaitingForReply] = useState(false)
  const [capReached, setCapReached] = useState(false)
  const [chatStatus, setChatStatus] = useState<{
    messageCount: number
    cap: number
    freeRemaining: number
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [roomToken, setRoomToken] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [answersOpen, setAnswersOpen] = useState(false)

  const lastSeqRef = useRef<number>(-1)
  const waitStartedAtRef = useRef<number | null>(null)

  const loadStatusRef = useRef<() => Promise<void>>()
  const loadMessagesRef = useRef<() => Promise<void>>()

  const twinName = profile.name
  const twinImageUrl = profile.image_url
  const { displayUrl: twinHeaderThumbUrl, handleImageError: handleTwinHeaderImageError } =
    usePersonaThumbFallback(twinImageUrl, 64)

  const demographics = (profile.respondent_attributes ?? {}) as Record<string, string>
  const locationLine = buildLocationLine(demographics)
  const bio = profile.summary || null

  const surveyPairs = useMemo(
    () => resolveSurveyAnswerPairs(
      extractCleanSurveyAnswers(profile.survey_data ?? {}),
      surveyQuestionsMap,
      surveyChoiceLabels,
    ),
    [profile.survey_data, surveyQuestionsMap, surveyChoiceLabels],
  )
  const hasSurveyAnswers = surveyPairs.length > 0

  loadStatusRef.current = useCallback(async () => {
    try {
      const status = await publicApi.getChatStatus(shareToken, participantId)
      if (status.roomToken) {
        setRoomToken(status.roomToken)
      }
      setChatStatus({
        messageCount: status.messageCount,
        cap: status.cap,
        freeRemaining: status.freeRemaining,
      })
    } catch {
      // Non-critical
    }
  }, [shareToken, participantId])

  loadMessagesRef.current = useCallback(async () => {
    try {
      const { messages: newMessages } = await publicApi.getChatMessages(
        shareToken,
        lastSeqRef.current >= 0 ? lastSeqRef.current : undefined,
        participantId,
      )
      if (newMessages.length > 0) {
        setMessages((prev) => {
          const real = prev.filter((m) => Number.isInteger(m.sequenceIndex))
          const existingIndices = new Set(real.map((m) => m.sequenceIndex))
          const toAdd = newMessages.filter(
            (m) => !existingIndices.has(m.sequenceIndex),
          )
          if (toAdd.length === 0 && real.length === prev.length) return prev
          const merged = [...real, ...toAdd].sort(
            (a, b) => a.sequenceIndex - b.sequenceIndex,
          )
          return merged
        })

        lastSeqRef.current = newMessages[newMessages.length - 1].sequenceIndex

        const hasTwinReply = newMessages.some(endsWaitingForReply)
        if (hasTwinReply) {
          setWaitingForReply(false)
        }
      }
    } catch {
      // Retry on the next stall repair.
    }
  }, [shareToken, participantId])

  useEffect(() => {
    let cancelled = false
    async function init() {
      try {
        const { messages: existing } = await publicApi.getChatMessages(
          shareToken,
          undefined,
          participantId,
        )
        if (cancelled) return
        if (existing.length > 0) {
          setMessages(existing)
          lastSeqRef.current = existing[existing.length - 1].sequenceIndex
        } else {
          try {
            const init = await publicApi.initChat(shareToken, participantId)
            if (init.roomToken) {
              setRoomToken(init.roomToken)
            }
            if (cancelled) return
            const { messages: afterInit } = await publicApi.getChatMessages(
              shareToken,
              undefined,
              participantId,
            )
            if (cancelled) return
            // Rooms start empty (no generated greeting), so an empty room shows
            // the empty state instead of waiting for a twin reply.
            if (afterInit.length > 0) {
              setMessages(afterInit)
              lastSeqRef.current = afterInit[afterInit.length - 1].sequenceIndex
            }
          } catch {
            // Init failed: the empty state stays; the first send retries the room.
          }
        }
      } catch {
        // No history — fresh start
      }
      if (!cancelled) {
        setReady(true)
        loadStatusRef.current?.()
      }
    }
    init()
    return () => { cancelled = true }
  }, [shareToken, participantId])

  useRealtimeRepair({
    active: waitingForReply,
    onRepair: (reason) => {
      incrementPollWonRepair('PUBLIC', reason)
      loadMessagesRef.current?.()
      loadStatusRef.current?.()
    },
    stallTimeoutMs: 15_000,
  })

  useGuestChatRoomSocket({
    roomToken,
    onMessage: (message) => {
      lastSeqRef.current = Math.max(lastSeqRef.current, message.sequenceIndex)
      setMessages((prev) => {
        if (prev.some((m) => m.sequenceIndex === message.sequenceIndex)) {
          return prev
        }
        return [
          ...prev.filter((m) => Number.isInteger(m.sequenceIndex)),
          {
            sequenceIndex: message.sequenceIndex,
            senderType: message.senderType as ChatMessage['senderType'],
            senderParticipantId: message.senderParticipantId || null,
            content: message.content,
            mode: (message.mode as ChatMessage['mode']) ?? null,
            metadataType: message.metadataType,
            metadata: message.metadata,
            createdAt: message.createdAt,
          },
        ]
      })
      if (endsWaitingForReply(message)) {
        setWaitingForReply(false)
      }
    },
    onTurnComplete: () => {
      setWaitingForReply(false)
      loadStatusRef.current?.()
    },
  })

  useEffect(() => {
    if (!waitingForReply) {
      waitStartedAtRef.current = null
      return
    }
    waitStartedAtRef.current = Date.now()
    const timeout = window.setTimeout(() => {
      if (
        waitStartedAtRef.current !== null &&
        Date.now() - waitStartedAtRef.current >= MAX_WAIT_MS
      ) {
        setWaitingForReply(false)
        setError('The twin is taking longer than expected. Please try again.')
      }
    }, MAX_WAIT_MS)
    return () => window.clearTimeout(timeout)
  }, [waitingForReply])

  const handleSend = useCallback(async (content: string) => {
    if (!content.trim() || capReached || waitingForReply) return

    setError(null)

    setMessages((prev) => [
      ...prev,
      {
        sequenceIndex: (lastSeqRef.current ?? -1) + 0.5,
        senderType: 'user',
        senderParticipantId: null,
        content,
        mode: 'chat',
        metadataType: null,
        metadata: null,
        createdAt: new Date().toISOString(),
      },
    ])
    setWaitingForReply(true)

    try {
      const sent = await publicApi.sendChatMessage(shareToken, content, undefined, participantId)
      if (sent.roomToken) {
        setRoomToken(sent.roomToken)
      }
      await loadMessagesRef.current?.()
      await loadStatusRef.current?.()
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'capReached' in err) {
        setCapReached(true)
        setWaitingForReply(false)
        setError(null)
      } else {
        setError(blockedFeatureMessage(err) ?? (err instanceof Error ? err.message : t('studyReport.sendFailed')))
        setWaitingForReply(false)
      }
    }
  }, [shareToken, participantId, capReached, waitingForReply])

  const twinInitials = getUserInitials(twinName)
  const scrollKey = messages.length + (waitingForReply ? 1 : 0)
  const remaining = chatStatus ? chatStatus.cap - chatStatus.messageCount : null

  const ChevronIcon = sidebarOpen ? ChevronUp : ChevronDown
  // Before the first message the twin's card and the composer sit centered together.
  const isCenteredEmpty = messages.length === 0 && !waitingForReply && !capReached
  // While empty the prompt lives in the composer's placeholder instead of a headline.
  const emptyHeadline = t('chatChrome.askTwinAnything', { name: twinName })

  const statusLine = chatStatus && !capReached && remaining !== null && remaining > 0 ? (
    <div className="shrink-0 px-6 py-1.5">
      <Text size="xs" className="text-twin-chat-fg-muted">
        {t('chatChrome.messageRemaining', { count: remaining })}
      </Text>
    </div>
  ) : null

  const errorLine = error ? (
    <div className="shrink-0 px-6 py-1.5">
      <Text size="sm" className="text-destructive">{error}</Text>
    </div>
  ) : null

  const renderComposer = (hero: boolean) => (
    <div className="shrink-0 px-4 pb-4">
      <ChatComposer
        hero={hero}
        onSend={handleSend}
        disabled={waitingForReply}
        placeholder={
          waitingForReply
            ? t('chatChrome.twinThinking', { name: twinName })
            : hero
              ? emptyHeadline
              : t('chatChrome.messageTwin', { name: twinName })
        }
        dataTestId="guest-chat-composer"
        textareaDataTestId="guest-chat-composer-input"
      />
    </div>
  )

  if (!ready) {
    return (
      <div className="flex flex-1 flex-col min-h-0 bg-twin-chat-bg rounded-2xl overflow-hidden items-center justify-center">
        <Text size="sm" className="text-twin-chat-fg-muted">{t('loading', { ns: 'common' })}</Text>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col min-h-0 bg-twin-chat-bg rounded-2xl overflow-hidden">
      {/* ── Full-width header ── */}
      <div className="shrink-0 border-b border-twin-chat-divider/20">
        <div className="grid grid-cols-3 items-center px-6 py-3">
          {/* Left: back + sidebar toggle */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={t('studyReport.backToTwins')}
              data-testid="guest-chat-back"
              onClick={onBack}
              className="flex items-center justify-center rounded-md p-1 transition-colors hover:bg-twin-chat-bubble/20 text-twin-chat-fg-muted hover:text-twin-chat-fg"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>

            <button
              type="button"
              data-testid="guest-chat-sidebar-toggle"
              aria-label={sidebarOpen ? t('chatChrome.collapseSidebar') : t('chatChrome.expandSidebar')}
              onClick={() => setSidebarOpen((o) => !o)}
              className="flex items-center gap-1.5 rounded-md px-1 py-0.5 transition-colors hover:bg-twin-chat-bubble/20"
            >
              <Text size="sm" className="font-medium text-twin-chat-fg">
                {t('chatChrome.interviewWith', { name: twinName })}
              </Text>
              <ChevronIcon className="h-3.5 w-3.5 shrink-0 text-twin-chat-muted" />
            </button>
          </div>

          {/* Center: twin avatar + name */}
          <div className="flex items-center justify-center gap-2">
            {twinHeaderThumbUrl && (
              <img
                src={twinHeaderThumbUrl}
                alt={twinName}
                className="h-8 w-8 shrink-0 rounded-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  handleTwinHeaderImageError(() => {
                    target.src = FALLBACK_IMAGE
                  })
                }}
              />
            )}
          </div>

          {/* Right: survey answers toggle */}
          <div className="flex items-center justify-end gap-2">
            {hasSurveyAnswers && (
              <button
                type="button"
                data-testid="guest-survey-answers-toggle"
                onClick={() => setAnswersOpen((o) => !o)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  answersOpen
                    ? 'text-twin-chat-fg bg-twin-chat-bubble/30'
                    : 'text-twin-chat-fg-muted hover:text-twin-chat-fg hover:bg-twin-chat-bubble/30',
                )}
              >
                <ClipboardList className="h-3.5 w-3.5" />
                {t('studyReport.surveyAnswers')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Body: sidebar + chat + survey answers ── */}
      <div className="relative flex min-h-0 flex-1 overflow-hidden">

        {/* ── Sidebar panel — overlays from the left ── */}
        <div
          className={cn(
            'absolute inset-y-0 left-0 z-10 hidden flex-col transition-[transform,opacity] duration-300 ease-in-out lg:flex',
            sidebarOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0 pointer-events-none',
          )}
        >
          <div className="flex min-h-0 w-[360px] flex-1 flex-col p-5 pt-0 pr-0">
            <div className="flex flex-1 flex-col overflow-hidden rounded-2xl bg-twin-chat-surface">
              <div className="flex-1 overflow-y-auto scrollbar-overlay p-5 flex flex-col gap-4">
                {/* Hero image */}
                <div
                  data-testid="guest-sidebar-hero"
                  className="h-[220px] w-full shrink-0 overflow-hidden rounded-xl bg-twin-chat-bubble/40"
                >
                  {twinImageUrl ? (
                    <img
                      src={twinImageUrl}
                      alt={twinName}
                      className="h-full w-full object-cover object-top"
                      onError={(e) => {
                        ;(e.target as HTMLImageElement).src = FALLBACK_IMAGE
                      }}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-twin-chat-bubble/60">
                      <span className="text-5xl font-bold text-twin-chat-fg/40">
                        {twinName[0]}
                      </span>
                    </div>
                  )}
                </div>

                {/* Name + location */}
                <div className="flex flex-col gap-1">
                  <Heading
                    level={2}
                    data-testid="guest-sidebar-name"
                    className="text-2xl leading-[1.4] text-twin-chat-fg font-display"
                  >
                    {twinName}
                  </Heading>
                  {locationLine && (
                    <div className="flex items-center gap-1 text-twin-chat-muted">
                      <MapPin className="h-4 w-4" />
                      <Text size="sm" className="text-twin-chat-muted">
                        {locationLine}
                      </Text>
                    </div>
                  )}
                </div>

                {/* Bio */}
                {bio && (
                  <>
                    <SidebarDivider />
                    <CollapsibleText text={bio} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Chat column — always centered ── */}
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col">
            <ConversationStage
              isEmpty={isCenteredEmpty}
              emptyHero={
                <ConversationEmptyState
                  twins={[{ id: profile.respondent_id, name: twinName, imageUrl: twinImageUrl }]}
                  headline={emptyHeadline}
                />
              }
              thread={
            <ChatScrollContainer
              scrollKey={scrollKey}
              className="flex-1 min-h-0 px-4 py-4 space-y-4"
              data-testid="guest-chat-messages"
            >
              {messages.map((msg, idx) => {
                const isUser = msg.senderType === 'user'
                const total = messages.length
                const position = getBubblePosition(idx, total)
                const answerGrounding =
                  msg.senderType === 'twin' ? readAnswerGrounding(msg.metadata) : null

                return (
                  <ChatBubble
                    key={`${msg.sequenceIndex}-${idx}`}
                    role={isUser ? 'user' : 'assistant'}
                    content={msg.content}
                    position={position}
                    avatar={
                      !isUser
                        ? {
                            url: twinImageUrl ?? null,
                            fallbackInitials: twinInitials,
                            seed: twinName,
                          }
                        : undefined
                    }
                    senderInfo={
                      !isUser && idx === 0
                        ? { name: twinName }
                        : undefined
                    }
                    belowAvatar={
                      answerGrounding ? (
                        <AnswerGroundingMeter
                          messageId={`seq-${msg.sequenceIndex}`}
                          level={answerGrounding}
                        />
                      ) : undefined
                    }
                  />
                )
              })}

              {waitingForReply && (
                <ChatBubble
                  role="assistant"
                  content=""
                  position="solo"
                  avatar={{
                    url: twinImageUrl ?? null,
                    fallbackInitials: twinInitials,
                    seed: twinName,
                  }}
                  extraContent={
                    <div className="flex items-center">
                      <ChatTypingDots />
                    </div>
                  }
                />
              )}
            </ChatScrollContainer>
              }
              beforeComposer={<>{statusLine}{errorLine}</>}
              composer={capReached ? (
              <div className="shrink-0 px-4 pb-4">
                <div className="flex flex-col items-center gap-3 py-4 px-6 rounded-2xl bg-twin-chat-surface">
                  <UserPlus className="w-5 h-5 text-twin-chat-fg-muted" />
                  <Text size="sm" className="text-center font-medium text-twin-chat-fg">
                    {t('chatChrome.messageLimitReached')}
                  </Text>
                  <Text size="xs" className="text-center text-twin-chat-fg-muted">
                    {t('chatChrome.signUpToContinue')}
                  </Text>
                  <Button asChild variant="primary" size="sm" className="mt-1 text-sm">
                    <a href="/register" target="_blank" rel="noopener noreferrer">
                      {t('chatChrome.signUpFree')}
                    </a>
                  </Button>
                </div>
              </div>
            ) : (
              renderComposer(isCenteredEmpty)
            )}
            />
          </div>
        </div>

        {/* ── Survey Answers panel — overlays from the right ── */}
        {hasSurveyAnswers && (
          <div
            className={cn(
              'absolute inset-y-0 right-0 z-10 hidden flex-col transition-[transform,opacity] duration-300 ease-in-out lg:flex',
              answersOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none',
            )}
          >
            <div className="flex min-h-0 w-[360px] flex-1 flex-col p-5 pt-0 pl-0">
              <GuestSurveyAnswersPanel
                pairs={surveyPairs}
                onClose={() => setAnswersOpen(false)}
                className="flex-1"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
